import { useEffect, useState, useCallback } from 'react';

import withAuth from '../../HOCs/withAuth';
import TripInvitationList from '../trip-invitations/trip-invitation-list';
import {
  ListUserInvitations,
  ListUserSentInvitations,
  AcceptTripInvitation,
  DenyTripInvitation
} from '../../services/trip-invitation.service';
import TripsInvitationsNavBar from '../navbars/trip-invitations-navbar';
import defaultImage from '../../Assets/trips/trip-1.png';

import { TripAcceptedInvitationCardProps } from '../types/card';
import Button from '../UI/button';
import CollaboratorAddModal from '../modals/collaborators/collaborator-add-modal';
import {
  TripInvitationProvider,
  useTripInvitation
} from '../contexts/trip-invitation-context';
import TripInvitationNew from '../trip-invitations/trip-invitaion-new';

function TripInvitationsPage() {
  const [invitations, setInvitations] = useState<any[]>([]);
  const [sentInvitations, setSentInvitations] = useState<any[]>([]);
  const [showAddFolder, setShowAddFolder] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { refreshInvitations, setRefreshInvitations } = useTripInvitation();

  const handleAddCollaboratorModal = (refresh: boolean) => {
    setShowAddFolder(false);
    setRefreshInvitations(refresh);
  };

  const handleAccept = async (tripId: string, invitationId: string) => {
    try {
      await AcceptTripInvitation(tripId, invitationId);
      setRefreshInvitations(true);
    } catch (err) {
      console.error('Failed to accept invitation', err);
      setError('Failed to accept invitation');
    }
  };

  const handleDecline = async (tripId: string, invitationId: string) => {
    try {
      await DenyTripInvitation(tripId, invitationId);
      setRefreshInvitations(true);
    } catch (err) {
      console.error('Failed to decline invitation', err);
      setError('Failed to decline invitation');
    }
  };

  const LoadInvitations = useCallback(async () => {
    try {
      const [receivedInvitations, sentInvitationsList] = await Promise.all([
        ListUserInvitations(),
        ListUserSentInvitations()
      ]);
      setInvitations(receivedInvitations || []);
      setSentInvitations(sentInvitationsList || []);
    } catch (err) {
      setError('Failed to load invitations');
    }
  }, []);

  const LoadAllInvitations = useCallback(async () => {
    setLoading(true);
    await LoadInvitations();
    setLoading(false);
  }, [LoadInvitations]);

  useEffect(() => {
    LoadAllInvitations();
  }, [LoadAllInvitations]);

  useEffect(() => {
    if (refreshInvitations) {
      LoadAllInvitations();
      setRefreshInvitations(false);
    }
  }, [LoadAllInvitations, refreshInvitations, setRefreshInvitations]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div
      className='overflow-y-auto'
      style={{ height: 'calc(100vh - var(--header-height))' }}
    >
      <div
        className='trips-container container-px'
        style={{ paddingBottom: '100px' }}
      >
        <TripsInvitationsNavBar />
        <div className='invitation-list-new'>
          {sentInvitations.map((item) => (
            <TripInvitationNew
              key={item.invitationId}
              imageUrl={item.imageUrl || defaultImage}
              title={item.tripTitle}
              createdBy={item.ownerUsername}
              collaboratorsCount={item.collaboratorsCount}
              description={item.description}
              createdAt={new Date(item.createdAt).toLocaleDateString()}
              alertPartners={`Invitation sent to ${item.inviteeEmail} - Status: ${item.status}`}
              onAccept={() => {}} // Sent invitations don't have accept/decline
              onDecline={() => {}}
              hideSentInvitationActions
            />
          ))}
        </div>
        <div
          className='invitation-list-new'
          style={{ marginTop: '32px' }}
        >
          {invitations.map((item) => (
            <TripInvitationNew
              key={item.invitationId}
              imageUrl={item.imageUrl || defaultImage}
              title={item.tripTitle}
              createdBy={item.ownerUsername}
              collaboratorsCount={item.collaboratorsCount}
              description={item.description}
              createdAt={new Date(item.createdAt).toLocaleDateString()}
              alertPartners={`This invitation expires in ${item.daysUntilExpiration} days!`}
              onAccept={() => handleAccept(item.tripId, item.invitationId)}
              onDecline={() => handleDecline(item.tripId, item.invitationId)}
            />
          ))}
        </div>
        <CollaboratorAddModal
          show={showAddFolder}
          handleClose={() => handleAddCollaboratorModal(true)}
          tripId={''}
        />
      </div>
    </div>
  );
}

const TripsInvitationPageWithProvider: React.FC = () => {
  return (
    <TripInvitationProvider>
      <TripInvitationsPage />
    </TripInvitationProvider>
  );
};

export default withAuth(TripsInvitationPageWithProvider);
